/**
 * MappingOutline — "what enters the case", always in view: every entity with the identifier
 * that decides which rows and files become the same entity, every relationship with how many
 * it makes, and the searchable row text. Counts follow each edit through the preview. Pure:
 * the review state and the selection in; edits and selections out.
 */

import { ArrowRight, Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { formatCount } from '@/components/case-dashboard/dashboard-view'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import type { OutputEdge, OutputSummary } from '@/services/api/structuredIngest'
import { cn } from '@/utils/cn'
import { IdentityKey } from './IdentityKey'
import { HelpTip, KindDot, Spellings } from './recipe-cards'
import { grainPhrase } from './recipe-graph-data'
import { countPhrase, type EntityReview, type ReviewState, type WindowOutput } from './review-state'

interface MappingOutlineProps {
  data: {
    summary: OutputSummary
    review: Pick<ReviewState, 'entities' | 'windows'>
    rowsTotal: number
    /** A recompute is in flight: the counts lag the last edit by a moment. */
    updating: boolean
  }
  selection: { id: string | null; onSelect: (id: string) => void }
  actions: {
    onToggle: (name: string, enabled: boolean) => void
    onToggleWindows: (enabled: boolean) => void
  }
}

/** Entity and relationship rows share one grid: on/off, what it is, how it is built, how many. */
const ROW =
  'grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1.5 px-3 py-2.5 md:grid-cols-[auto_minmax(10rem,14rem)_minmax(0,1fr)_minmax(7rem,auto)]'

function OutlineGroup({
  title,
  header,
  children,
}: {
  title: string
  /** What each column of the rows holds, above them. */
  header?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex flex-col">
      <h4 className="px-3 pb-1.5 text-sm font-semibold">{title}</h4>
      {header}
      <ul className="divide-y divide-border/60 rounded-lg border border-border/60">{children}</ul>
    </div>
  )
}

/** The entity rows' columns on wide screens; a phone labels the identifier in each row instead. */
function EntityColumns() {
  const { t } = useTranslation()
  return (
    <div className={cn(ROW, 'py-1.5 text-xs font-medium text-muted-foreground max-md:hidden')}>
      <span aria-hidden className="w-4" />
      <span>{t('tableMapping.outline.heading.entity')}</span>
      <span className="inline-flex items-center gap-1.5">
        {t('tableMapping.card.identifier')}
        <HelpTip wide>{t('tableMapping.card.identifierHelp')}</HelpTip>
      </span>
      <span className="text-right">{t('tableMapping.outline.heading.count')}</span>
    </div>
  )
}

function Include({
  name,
  enabled,
  onToggle,
}: {
  name: string
  enabled: boolean
  onToggle: (enabled: boolean) => void
}) {
  const { t } = useTranslation()
  return (
    <Checkbox
      className="mt-0.5"
      checked={enabled}
      onCheckedChange={value => onToggle(value === true)}
      aria-label={t('tableMapping.outline.include', { name })}
    />
  )
}

/** The count column: "Off", "?" before the first count, or the figure — and what it is out of. */
function Count({
  enabled,
  count,
  note,
}: {
  enabled: boolean
  count: number | null
  note?: ReactNode
}) {
  const { t, i18n } = useTranslation()
  const value = !enabled
    ? t('tableMapping.outline.off')
    : count === null
      ? '?'
      : formatCount(count, i18n.language)
  return (
    <div className="col-start-2 flex flex-col gap-0.5 text-xs md:col-start-auto md:items-end md:text-right">
      <span className="text-sm font-medium tabular-nums">{value}</span>
      {enabled && note && <span className="text-muted-foreground">{note}</span>}
    </div>
  )
}

function EntityRow({
  entity: { node, identity, stats },
  rowsTotal,
  selection,
  onToggle,
}: {
  entity: EntityReview
  rowsTotal: number
  selection: MappingOutlineProps['selection']
  onToggle: (enabled: boolean) => void
}) {
  const { t } = useTranslation()
  const selected = selection.id === node.name
  const note =
    node.source === 'subject' ? (
      t('tableMapping.outline.wholeFile')
    ) : stats ? (
      <>
        {t('tableMapping.outline.inRows', { rows: stats.rows_with_identity, total: rowsTotal })}
        <Spellings stats={stats} />
      </>
    ) : (
      t('tableMapping.outline.afterUpdate')
    )
  return (
    <li className={cn(ROW, !node.enabled && 'opacity-60')}>
      <Include name={node.name} enabled={node.enabled} onToggle={onToggle} />
      <button
        type="button"
        onClick={() => selection.onSelect(node.name)}
        aria-pressed={selected}
        aria-label={t('tableMapping.outline.show', { name: node.name })}
        className="flex min-w-0 flex-col items-start gap-0.5 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span
          className={cn(
            'max-w-full truncate text-sm font-medium hover:underline',
            selected && 'text-primary'
          )}
          title={node.name}
        >
          {node.name}
        </span>
        <span className="flex max-w-full items-center gap-1.5 text-xs text-muted-foreground">
          <KindDot label={node.label} />
          <span className="truncate">{node.label}</span>
          {node.outsideSchema && (
            <Badge variant="warning">{t('tableMapping.outline.newType')}</Badge>
          )}
        </span>
      </button>
      <div className="col-start-2 min-w-0 md:col-start-auto">
        <p className="mb-1 text-xs font-medium text-muted-foreground md:hidden">
          {t('tableMapping.card.identifier')}
        </p>
        <IdentityKey identity={identity} />
      </div>
      <Count enabled={node.enabled} count={node.count} note={note} />
    </li>
  )
}

function RelationshipRow({
  edge,
  selected,
  onSelect,
  onToggle,
}: {
  edge: OutputEdge
  selected: boolean
  onSelect: () => void
  onToggle: (enabled: boolean) => void
}) {
  const { t } = useTranslation()
  const way = edge.kind === 'structural' ? t('tableMapping.grain.fact') : grainPhrase(edge.grain, t)
  return (
    <li className={cn(ROW, !edge.enabled && 'opacity-60')}>
      <Include name={edge.name} enabled={edge.enabled} onToggle={onToggle} />
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        aria-label={t('tableMapping.outline.show', { name: edge.name })}
        className="flex min-w-0 flex-wrap items-center gap-1 rounded-sm text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:col-span-2"
      >
        <span className="font-medium">{edge.from}</span>
        <ArrowRight aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
        <span
          className={cn(
            'rounded-full border px-2 py-0.5 font-mono text-xs',
            selected ? 'border-primary text-primary' : 'border-border text-muted-foreground'
          )}
        >
          {edge.type}
        </span>
        <ArrowRight aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="font-medium">{edge.to}</span>
        {edge.outsideSchema && <Badge variant="warning">{t('tableMapping.outline.newType')}</Badge>}
      </button>
      <Count
        enabled={edge.enabled}
        count={edge.count}
        note={[way, ...edge.measures.map(m => t('tableMapping.outline.sum', { column: m }))].join(
          ', '
        )}
      />
    </li>
  )
}

function RowText({
  windows,
  updating,
  onToggle,
}: {
  windows: WindowOutput
  updating: boolean
  onToggle: (enabled: boolean) => void
}) {
  const { t } = useTranslation()
  const columns = windows.textColumns
  return (
    <div className="flex flex-col gap-1.5 px-3">
      <label className="flex items-start gap-2 text-sm font-medium">
        <Checkbox
          className="mt-0.5"
          checked={windows.enabled}
          onCheckedChange={v => onToggle(v === true)}
        />
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            {t('tableMapping.outline.rowText.toggle')}
            <HelpTip wide>
              {t('tableMapping.outline.rowText.help')}
              {columns.length > 0 &&
                ` ${t('tableMapping.outline.rowText.builtFrom', {
                  columns: columns.slice(0, 8).join(', ') + (columns.length > 8 ? ' …' : ''),
                })}`}
            </HelpTip>
          </span>
          {windows.enabled && windows.count !== null && (
            <span
              className={cn(
                'font-normal text-muted-foreground max-md:basis-full',
                updating && 'opacity-60'
              )}
            >
              {t('tableMapping.count.snippets', { count: windows.count })}
            </span>
          )}
        </span>
      </label>
      {!windows.enabled && (windows.count ?? 0) > 0 && (
        <p className="text-xs text-warning-foreground">
          {t('tableMapping.outline.rowText.invisible', { count: windows.count ?? 0 })}
        </p>
      )}
    </div>
  )
}

export function MappingOutline({ data, selection, actions }: MappingOutlineProps) {
  const { t } = useTranslation()
  const edges = data.summary.edges
  return (
    <section className="flex flex-col gap-4" aria-live="polite">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-base font-semibold">{t('tableMapping.outline.title')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('tableMapping.outline.totals', {
            entities: countPhrase(t, 'entities', data.summary.totals.nodes),
            relationships: countPhrase(t, 'relationships', data.summary.totals.edges),
          })}
        </p>
        {data.updating && (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 aria-hidden className="size-3 animate-spin motion-reduce:animate-none" />
            {t('tableMapping.outline.updating')}
          </span>
        )}
      </div>
      <OutlineGroup title={t('tableMapping.outline.entities')} header={<EntityColumns />}>
        {data.review.entities.map(entity => (
          <EntityRow
            key={entity.node.name}
            entity={entity}
            rowsTotal={data.rowsTotal}
            selection={selection}
            onToggle={enabled => actions.onToggle(entity.node.name, enabled)}
          />
        ))}
      </OutlineGroup>
      <p className="-mt-2 px-3 text-xs text-muted-foreground">
        {t('tableMapping.outline.mergeNote')}
      </p>
      {edges.length > 0 && (
        <OutlineGroup title={t('tableMapping.outline.relationships')}>
          {edges.map(edge => (
            <RelationshipRow
              key={edge.name}
              edge={edge}
              selected={selection.id === edge.name}
              onSelect={() => selection.onSelect(edge.name)}
              onToggle={enabled => actions.onToggle(edge.name, enabled)}
            />
          ))}
        </OutlineGroup>
      )}
      <RowText
        windows={data.review.windows}
        updating={data.updating}
        onToggle={actions.onToggleWindows}
      />
    </section>
  )
}
